import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, Box, ScaleFade, Spinner, Center } from '@chakra-ui/react';

import { HeaderUp } from '../../../../components/_components_0.2/Header_0.2';
import { Sidebar } from '../../../../components/_components_0.2/Sidebar_0.2';
import { ExpensesTable } from '../../../../components/_components_0.2/Miles/ExpensesTable';
import { apllyToast } from '../../../../components/Global/Toast2.0';
import { expenseReportsService } from '../../../../services/expenseReportsService';
import { usersService } from '../../../../services/usersService';
import { useAuth } from '../../../../hooks/auth';

// Constantes — KM_RATE mantido para compatibilidade; usar getKmRate() para cálculo preciso
export {
  getKmRate,
  calculateKmValue,
  isDirectorSector,
  isFixedRateEligibleUser,
  isFixedRateKmObservation,
  recalculateKmGroupItems,
  SPECIAL_SECTOR_ID,
  SPECIAL_SECTOR_RATE,
} from '../../../../utils/kmRateTable';
export const KM_RATE = 1.63; // Valor padrão (faixa 2001-2050)

// Tipos de dados
export type SignatureStyle =
  | 'cursive-1'
  | 'cursive-2'
  | 'cursive-3'
  | 'elegant-1'
  | 'elegant-2';

export interface ISignature {
  name: string;
  style: SignatureStyle;
  // Dados da assinatura digital gov.br
  cpf?: string;
  signedAt?: string;
  certificate?: string;
}

export interface IReceipt {
  id?: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  // Campos do backend
  filename?: string;
  original_name?: string;
  mime_type?: string;
}

// Item individual de despesa dentro de um relatório
export interface IExpenseItemReceipt {
  id: string;
  item_id: string;
  receipt_type: 'receipt' | 'km_start' | 'km_end';
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  url: string;
  storage_path: string;
}

export interface IExpenseItem {
  id: string;
  expense_type:
    | 'Refeição'
    | 'Quilometragem adicional'
    | 'pedágio adicional'
    | 'Ajuda de custo'
    | 'Pedágio adicional'
    | 'Outros';
  amount?: number;
  additional_km?: number;
  km_start?: number;
  km_end?: number;
  displacement_reason?: string;
  receipt?: IReceipt | File;
  receipts?: IExpenseItemReceipt[]; // Array de anexos do backend
  receipt_km_start?: IReceipt | File;
  receipt_km_end?: IReceipt | File;
  observations?: string;
}

// Ajuda de custo é sempre o único item de um relatório (regra aplicada na
// criação). Usado para simplificar/ocultar seções que não se aplicam a ela
// (rateio por centro de custo) e para customizar o PDF gerado.
export const isAjudaCustoReport = (items: IExpenseItem[]): boolean =>
  items.length > 0 && items.every(i => i.expense_type === 'Ajuda de custo');

// Status da assinatura do relatório
export type SignatureStatus = 'pending' | 'sent' | 'signed' | 'rejected';

// Objeto user retornado pelo backend
export interface IExpenseReportUser {
  id: string;
  name: string;
  email: string;
  nickname?: string;
  job_position?: string;
  sector_id?: string;
  pix_key?: string;
  bank_name?: string;
  bank_agency?: string;
  bank_account?: string;
}

// Status do lançamento contábil
export type AccountingStatus =
  | 'EM PROCESSO'
  | 'AGUARDANDO LANCAMENTO'
  | 'LANCADO'
  | 'RECUSADO';

// Filial
export interface IBranch {
  id: string;
  short_name: string;
}

// Papel do participante dentro do relatório
export type ParticipantRole = 'responsible' | 'related';

// Participante do relatório (responsável + vinculados).
// Cada participante carrega seu próprio setor/seção/centro de custo/filial
// e dados bancários, pois o pagamento pode ser opcional (o responsável pode
// ter arcado com a despesa e ressarcir o vinculado depois).
export interface IExpenseParticipant {
  id: string; // uuid local (frontend) ou id do backend
  user_id: string;
  name?: string; // resolvido para exibição
  role: ParticipantRole;
  sector: string;
  section: string;
  cost_center: string;
  branch_id?: string;
  // Pagamento opcional: quando false, o responsável arcou com a despesa
  payment_required: boolean;
  pix_key?: string;
  bank_name?: string;
  bank_account?: string;
  bank_agency?: string;
  // Objeto user completo retornado pelo backend (relação eager)
  user?: IExpenseReportUser;
  // Cadeia de assinatura (responsável -> vinculados -> gestor)
  signing_order?: number;
  signature_status?: 'pending' | 'sent' | 'signed' | 'rejected';
  signature_name?: string;
  signature_style?: SignatureStyle;
  signature_cpf?: string;
  signed_at?: string;
}

// Forma de rateio do custo total entre centros de custo
export type RateioMode = 'percentual' | 'valores' | 'itens';

// Uma alocação de custo (centro de custo livre).
// O valor efetivo depende do modo do rateio:
//  - 'percentual': usa `percentage` (0-100)
//  - 'valores':    usa `value` (R$)
//  - 'itens':      usa `item_ids` (soma dos itens associados)
export interface IRateioAllocation {
  id: string;
  cost_center: string;
  sector?: string;
  section?: string;
  description?: string;
  percentage?: number;
  value?: number;
  item_ids?: string[];
}

// Rateio do relatório. A soma das alocações nunca pode ultrapassar o total das despesas.
export interface IExpenseRateio {
  mode: RateioMode;
  allocations: IRateioAllocation[];
}

// Relatório de ressarcimento (contém múltiplos itens de despesa)
export interface IExpenseReport {
  id: string;
  director: string; // user_id do diretor responsável
  director_name?: string; // nome do diretor (retornado pelo backend)
  user?: IExpenseReportUser; // objeto user completo retornado pelo backend
  // Gestor responsável pela aprovação
  manager_id?: string;
  manager?: IExpenseReportUser;
  // Filial
  branches_id?: string;
  branch_id?: string;
  branch?: IBranch;
  date: string;
  // Campos legados (nível-relatório) — espelham o participante responsável
  // para manter compatibilidade com o backend atual.
  sector: string;
  section: string;
  cost_center: string;
  // Participantes do relatório (responsável + vinculados). Cada um com seu
  // setor/seção/centro de custo/filial e dados de pagamento.
  participants?: IExpenseParticipant[];
  // Rateio do custo total entre centros de custo livres.
  rateio?: IExpenseRateio;
  // Dados bancários do usuário (preenchidos no ato da criação)
  pix_key?: string;
  bank_name?: string;
  bank_agency?: string;
  bank_account?: string;
  // Status do lançamento contábil
  status?: AccountingStatus;
  observation?: string; // Observação quando RECUSADO
  due_date?: string; // Vencimento (calculado pelo backend: 3 dias úteis após envio contábil; Ajuda de custo usa a data escolhida pelo usuário)
  accounting_sent_at?: string;
  items: IExpenseItem[]; // Múltiplas despesas em um único relatório
  // Assinatura do usuário
  signature?: ISignature; // Opcional - apenas após assinatura digital
  signature_status?: SignatureStatus;
  signatureStatus?: SignatureStatus;
  signature_requested_at?: string;
  signatureRequestedAt?: string;
  signed_at?: string;
  signedAt?: string;
  // Assinatura do gestor
  managerSignature?: ISignature;
  manager_signature_status?: SignatureStatus;
  managerSignatureStatus?: SignatureStatus;
  manager_signed_at?: string;
  managerSignedAt?: string;
  manager_signature_requested_at?: string;
  managerSignatureRequestedAt?: string;
  // Metadados
  created_at: string;
  updated_at: string;
  is_consolidated?: boolean;
  isConsolidated?: boolean;
  consolidated_at?: string;
  consolidatedAt?: string;
  consolidated_report_id?: string;
  consolidatedReportId?: string;
}

// Manter IExpense por compatibilidade (deprecated - usar IExpenseReport)
export interface IExpense extends IExpenseReport {
  expense_type?: string;
  amount?: number;
  additional_km?: number;
  displacement_reason?: string;
  receipt?: IReceipt | File;
  observations?: string;
}

export default function Expenses() {
  const { user } = useAuth();
  const [expenseReports, setExpenseReports] = useState<IExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Se flag_9 = true, usuário vê todos os relatórios; senão, apenas os que criou
  const visibleReports = useMemo(() => {
    if (user?.flag_9) return expenseReports;
    return expenseReports.filter(
      report => (report as any).created_by === user?.id,
    );
  }, [expenseReports, user]);

  // Carregar relatórios do backend
  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const response = await expenseReportsService.list({
        page: 1,
        take: 1000, // Carregar todos
        showConsolidated: true,
      });

      setExpenseReports(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
      apllyToast('error', 'Erro ao carregar relatórios de despesas');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carregar ao montar componente
  useEffect(() => {
    loadReports();
  }, [loadReports, refreshKey]);

  const handleAddExpenseReport = useCallback(
    async (data: Omit<IExpenseReport, 'id' | 'created_at' | 'updated_at'>) => {
      try {
        // Separar arquivos dos itens (receipt, fotos hodômetro)
        const files: { [index: number]: File } = {};
        const kmFiles: { itemIndex: number; type: string; file: File }[] = [];

        data.items.forEach((item, index) => {
          if (item.receipt && item.receipt instanceof File) {
            files[index] = item.receipt;
          }
          if (item.receipt_km_start && item.receipt_km_start instanceof File) {
            kmFiles.push({ itemIndex: index, type: 'km_start', file: item.receipt_km_start });
          }
          if (item.receipt_km_end && item.receipt_km_end instanceof File) {
            kmFiles.push({ itemIndex: index, type: 'km_end', file: item.receipt_km_end });
          }
        });

        const createdReport = await expenseReportsService.create(data, files);

        // Vencimento manual da Ajuda de custo: o endpoint de criação não
        // aceita esse campo, então é gravado logo em seguida via o mesmo
        // PUT que a tela de contabilidade já usa para definir due_date.
        if (data.due_date) {
          try {
            await expenseReportsService.update(createdReport.id, {
              due_date: data.due_date,
            });
          } catch (err) {
            console.error('Erro ao definir data de pagamento da ajuda de custo:', err);
          }
        }

        // Upload das fotos do hodômetro
        if (kmFiles.length > 0 && createdReport.items) {
          await Promise.all(
            kmFiles.map(async ({ itemIndex, type, file }) => {
              const createdItem = createdReport.items[itemIndex];
              if (!createdItem) return;
              try {
                await expenseReportsService.uploadAttachment(
                  createdReport.id,
                  createdItem.id,
                  file,
                  type,
                );
              } catch (err) {
                console.error(`Erro ao enviar foto hodômetro ${type}:`, err);
              }
            }),
          );
        }

        // Atualizar dados bancários do usuário na tabela accesses
        if (data.pix_key || data.bank_name || data.bank_agency || data.bank_account) {
          try {
            await usersService.updateBankData(data.director, {
              pix_key: data.pix_key,
              bank_name: data.bank_name,
              bank_agency: data.bank_agency,
              bank_account: data.bank_account,
            });
          } catch (err) {
            console.error('Erro ao atualizar dados bancários:', err);
          }
        }

        apllyToast('success', 'Relatório de despesas criado com sucesso!');
        setRefreshKey(prev => prev + 1);
      } catch (error: any) {
        console.error('Erro ao criar relatório:', error);
        apllyToast(
          'error',
          error.response?.data?.message || 'Erro ao criar relatório de despesas'
        );
      }
    },
    []
  );

  const handleEditExpenseReport = useCallback(
    async (id: string, data: Partial<IExpenseReport>) => {
      try {
        await expenseReportsService.update(id, data);
        apllyToast('success', 'Relatório atualizado com sucesso!');

        // Recarregar lista
        setRefreshKey(prev => prev + 1);
      } catch (error: any) {
        console.error('Erro ao atualizar relatório:', error);
        apllyToast(
          'error',
          error.response?.data?.message || 'Erro ao atualizar relatório'
        );
      }
    },
    []
  );

  const handleDeleteExpenseReport = useCallback(async (id: string) => {
    try {
      await expenseReportsService.delete(id);
      apllyToast('success', 'Relatório removido com sucesso!');

      // Recarregar lista
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Erro ao deletar relatório:', error);
      apllyToast(
        'error',
        error.response?.data?.message || 'Erro ao remover relatório'
      );
    }
  }, []);

  const handleConsolidateReports = useCallback(
    async (
      data: Omit<IExpenseReport, 'id' | 'created_at' | 'updated_at'>,
      originalReportIds: string[],
      period: { startDate: string; endDate: string }
    ) => {
      try {
        await expenseReportsService.consolidate({
          director: data.director,
          startDate: period.startDate,
          endDate: period.endDate,
          reportIds: originalReportIds,
          manager_id: data.manager_id,
        });

        apllyToast(
          'success',
          `Relatório consolidado criado! ${originalReportIds.length} ${
            originalReportIds.length === 1
              ? 'relatório marcado'
              : 'relatórios marcados'
          } como consolidado.`
        );

        // Recarregar lista
        setRefreshKey(prev => prev + 1);
      } catch (error: any) {
        console.error('Erro ao consolidar relatórios:', error);
        apllyToast(
          'error',
          error.response?.data?.message || 'Erro ao consolidar relatórios'
        );
      }
    },
    []
  );

  if (loading) {
    return (
      <Flex direction="column" h="100vh">
        <HeaderUp path="expenses" />
        <Flex w="100%" my="6" maxWidth={1480} mx="auto" pb={4} px="6">
          <Sidebar path="expenses" />
          <Box flex="1">
            <Center h="400px">
              <Spinner size="xl" color="blue.500" thickness="4px" />
            </Center>
          </Box>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction="column" h="100vh">
      <HeaderUp path="expenses" />

      <Flex w="100%" my="6" maxWidth={1480} mx="auto" pb={4} px="6">
        <Sidebar path="expenses" />
        <Box flex="1">
          <ScaleFade initialScale={0.9} in>
            <ExpensesTable
              expenseReports={visibleReports}
              onAdd={handleAddExpenseReport}
              onEdit={handleEditExpenseReport}
              onDelete={handleDeleteExpenseReport}
              onConsolidate={handleConsolidateReports}
              onRefresh={() => setRefreshKey(prev => prev + 1)}
              isAdmin={!!user?.flag_9}
            />
          </ScaleFade>
        </Box>
      </Flex>
    </Flex>
  );
}
